import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { TeamMember, TeamRole } from '@mockd/shared';
import { api, ApiError } from '../utils/apiClient';

type AssignableRole = Exclude<TeamRole, 'owner'>;

interface UseTeamMembersReturn {
  members: TeamMember[];
  loading: boolean;
  fetchMembers: (teamId: string) => Promise<void>;
  updateMemberRole: (teamId: string, userId: string, role: AssignableRole) => Promise<void>;
  removeMember: (teamId: string, userId: string) => Promise<void>;
}

export function useTeamMembers(): UseTeamMembersReturn {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchMembers = useCallback(async (teamId: string) => {
    setLoading(true);
    try {
      const result = await api.get<{ data: TeamMember[] }>(`/api/teams/${teamId}/members`);
      setMembers(result.data);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to load members';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateMemberRole = useCallback(async (teamId: string, userId: string, role: AssignableRole) => {
    // Optimistic update with rollback, matching useProjects' update pattern.
    const previous = members;
    setMembers((prev) => prev.map((m) => (m.userId === userId ? { ...m, role } : m)));
    try {
      const result = await api.patch<{ data: TeamMember }>(`/api/teams/${teamId}/members/${userId}`, { role });
      setMembers((prev) => prev.map((m) => (m.userId === userId ? result.data : m)));
      toast.success('Role updated');
    } catch (err) {
      setMembers(previous);
      const message = err instanceof ApiError ? err.message : 'Failed to update role';
      toast.error(message);
      throw err;
    }
  }, [members]);

  const removeMember = useCallback(async (teamId: string, userId: string) => {
    const previous = members;
    setMembers((prev) => prev.filter((m) => m.userId !== userId));
    try {
      await api.del(`/api/teams/${teamId}/members/${userId}`);
      toast.success('Member removed');
    } catch (err) {
      setMembers(previous);
      const message = err instanceof ApiError ? err.message : 'Failed to remove member';
      toast.error(message);
      throw err;
    }
  }, [members]);

  return { members, loading, fetchMembers, updateMemberRole, removeMember };
}
